import { connect, ConnectedProps } from 'react-redux'
import { RenderQuoteStatus } from '../../../../../../app/erp/screens/Logistic/QuoteParent/subcomponents'
import { DisplayPriceGettingParams } from '../../../../../../baseComponents/Displays/DisplayPrice'
import { TwinTableLink } from '../../../../../../baseComponents/TwinTable'
import { TableComponentBaseScreenInnerProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { CustomRenderString, CustomRenderDate } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import locations from '../../../../../../utils/globals/locations'
import withLoading from '../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import { SellTagsPayload } from '../../../../../../utils/reducers/sells/sellTags'
import { RenderSellTags } from '../../../../../sells/CustomRenderTable'
import { SellOrderProductModelType } from '@teinor/erp/types/company/order/sell/sellOrder/sellOrderProduct'

type TableProductSellOrdersProps = TableComponentBaseScreenInnerProps & ReduxTableProductSellOrders & {}

const TableProductSellOrders: React.FC<TableProductSellOrdersProps> = ({ sellTags, setOpened, setTableInstance, extraWhereParams }) => {
    const { t } = useTwinTranslation()
    const sellOrderStatusComplex: TwinDictionary = {
        0: { name: t('draft', 'Borrador'), id: 0 },
        1: { name: t('preparing', 'Preparando'), id: 1 },
        3: { name: t('finished', 'Finalizado'), id: 3 },
    }
    const columns: ColumnTableSchema<SellOrderProductModelType> = [
        {
            id: 'OrderId',
            dataKey: 'OrderId',
            label: t('orderQuoteP', 'Pedido'),
            sortable: true,
            width: 120,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.Order?.full_assigned_number} className={allRowData?.Order?.assigned_number ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('orderQuoteP', 'Pedido') }
            },
        },
        {
            id: 'custom_name',
            dataKey: 'OrderId',
            label: t('name', 'Nombre'),
            simpleLabel: t('custom_name', 'Nombre personalizado'),
            sortable: true,
            minWidth: 120,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('custom_name', 'Nombre personalizado') }
            },
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.Order?.custom_name} />,
        },
        {
            id: 'CustomerId',
            dataKey: 'OrderId',
            label: t('customer', 'Clientes'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Order?.Customer?.fiscal_name || allRowData?.Order?.Customer?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            },
            searchKey: 'Order.Customer.name',
        },
        {
            id: 'status',
            dataKey: 'OrderId',
            label: t('status', 'Estado'),
            minWidth: 140,
            sortable: true,
            customRender: (parameterValue, allRowData) => <RenderQuoteStatus value={allRowData?.Order?.status} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: sellOrderStatusComplex, label: t('statusQuote', 'Estado del presupuesto') }

            },
        },
        {
            id: 'WorkingCenterId',
            dataKey: 'OrderId',
            searchKey: 'WorkingCenter.name',
            minWidth: 115,
            label: t('workingCenter', 'Centro de trabajo'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Order?.WorkingCenter?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('workingCenter', 'Centro de trabajo') }
            },
            hideColumnByDefault: true
        },
        {
            id: 'processed_date',
            dataKey: 'OrderId',
            label: t('createdDate', 'Fecha de creación'),
            minWidth: 165,
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={allRowData?.Order?.processed_date || allRowData?.Order?.createdAt} className={parameterValue ? '' : 'text-red-BA'} />,
            sortable: true,
        },
        {
            id: 'initialUnitPriceWithoutTax',
            dataKey: 'initialUnitPriceWithoutTax',
            label: t('unitPriceShort', 'Precio U.'),
            simpleLabel: t('initialUnitPriceWithoutTax', 'Precio unidad sin iva'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.Order?.CurrencyId} />,
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            sortable: true,
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
            width: 100,
            hideColumnByDefault: true,
        },
        {
            id: 'calculatedQuantity',
            dataKey: 'calculatedQuantity',
            label: t('quantityCalculatedS', 'Cant. calc.'),
            toolTip: t('quantityCalculated', 'Cantidad calculada'),
            simpleLabel: t('quantityCalculated', 'Cantidad calculada'),
            hideColumnByDefault: true,
            sortable: true,
        },
        {
            id: 'rowPriceWithTaxAndTotalDiscount',
            dataKey: 'rowPriceWithTaxAndTotalDiscount',
            label: t('totalPrice', 'Precio total'),
            simpleLabel: t('rowPriceWithTaxAndTotalDiscount', 'Precio total con impuestos'),
            hideColumnByDefault: true,
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.Order?.CurrencyId} />,
        },
        {
            id: 'rowPriceWithoutTaxAndTotalDiscount',
            dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
            label: t('totalPrice', 'Precio total'),
            simpleLabel: t('rowPriceWithoutTaxAndTotalDiscount', 'Precio total sin impuestos'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.Order?.CurrencyId} />,
        },
        {
            id: 'totalWithTaxWithComplements',
            dataKey: 'totalWithTaxWithComplements',
            simpleLabel: t('totalProductWithComplementsAndVat', 'Total producto con complementos e imp.'),
            label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.Order?.CurrencyId} />,
            width: 160,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
        },
        {
            id: 'totalWithoutTaxWithComplements',
            dataKey: 'totalWithoutTaxWithComplements',
            simpleLabel: t('totalProductWithComplements', 'Total Producto con complementos'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.Order?.CurrencyId} />,
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplements', 'Total con complementos')
        },
        {
            id: 'totalWithoutTaxWithComplementsPrev',
            dataKey: 'totalWithoutTaxWithComplementsPrev',
            simpleLabel: t('totalProductWithoutTaxWithComplementsPrev', 'Total producto con complementos antes de desc.'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.Order?.CurrencyId} />,
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
        },
        {
            id: 'unitPriceWithComplements',
            dataKey: 'unitPriceWithComplements',
            simpleLabel: t('unitPriceWithComplements', 'Precio unitario con complementos'),
            label: () => <div className='text-right w-full'> {t('unitPricewithComplementsS', 'P. U. comp.')}</div>,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.Order?.CurrencyId} />,
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('unitPriceWithComplements', 'Precio unitario con complementos'),
        },
        {
            id: 'finished_date',
            dataKey: 'OrderId',
            label: t('finishedDateLittleShort', 'Fecha finalización'),
            hideColumnByDefault: true,
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={allRowData?.Order?.finished_date} />,
            sortable: true,
            minWidth: 165
        },
        {
            id: 'OrderTags',
            dataKey: 'OrderId',
            label: t('tags', 'Etiquetas'),
            customRender: (_parameterValue, allRowData) => <RenderSellTags allRowData={allRowData?.Order || {}} {...{ sellTags }} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: sellTags?.sell || {}, label: t('tags', 'Etiquetas') }
            },
            generalSearchable: false,
            sortable: false,
            hideColumnByDefault: true
        },
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.sellOrderSingle, ['$OrderId']]} columns={columns} name='productOrders' getDataFrom='/api/app/order/sell/sellOrder/orderProduct/getAllSellOrderProductsByProductId'
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData,
            })} setTableInstance={setTableInstance} extraWhereParams={extraWhereParams}
        >
        </TwinTableLink>
    )
}

const TableProductSellOrdersDispatch = {
    setTags: (payload: SellTagsPayload) => ({ type: 'CHANGE_SELL_TAGS', payload }),
}

export type ReduxTableProductSellOrders = ConnectedProps<typeof tableProductSellOrdersConnect>
const mapTableProductSellOrdersConnector = (state: AllReduxPayloads) => ({ sellTags: state.sellTags })
const tableProductSellOrdersConnect = connect(mapTableProductSellOrdersConnector, TableProductSellOrdersDispatch)
const TableProductSellOrdersConnectLoading = withLoading(TableProductSellOrders, [{ fetchUrl: '/api/app/order/sell/sellTag/getAllSellTags', propName: 'sellTags', setFunctionName: 'setTags' }])
const TableProductSellOrdersConnect = tableProductSellOrdersConnect(TableProductSellOrdersConnectLoading)

export default TableProductSellOrdersConnect